import { Aide } from '@/components/ui/aide';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  FileDown,
  FileText,
  Gauge,
  MapPin,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { clientPortalAPI } from '../lib/api';
import { downloadBlob } from '../lib/download';
import { formatVolume } from '../lib/utils';
import { formatDateHeure } from '../lib/formats';
import { libelle, referenceLisible, texteSur } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { PageHeader } from '../components/ui/page-header';
import { LoadingState } from '../components/ui/loading-state';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePagination } from '../hooks/usePagination';

const inMotionStatuses = new Set([
  'EN_ROUTE_TO_BORDER',
  'EN_ROUTE_TO_LOADING',
  'EN_ROUTE_TO_ENTRY_CUSTOMS',
  'EN_ROUTE_TO_DELIVERY',
  'IN_TRANSIT',
]);

export default function ClientPortalPage() {
  const [missions, setMissions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [missionItems, documentItems] = await Promise.all([
        clientPortalAPI.getMissions(),
        clientPortalAPI.getDocuments(),
      ]);
      setMissions(Array.isArray(missionItems) ? missionItems : []);
      setDocuments(Array.isArray(documentItems) ? documentItems : []);
    } catch (err) {
      toast.error(messageErreur(err, 'Le portail client est momentanément indisponible.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const metrics = useMemo(() => {
    const active = missions.filter((item) => inMotionStatuses.has((item.trip?.status || '').toUpperCase())).length;
    const delivered = missions.filter((item) => ['DELIVERED', 'COMPLETED', 'BILLING_READY'].includes((item.trip?.status || '').toUpperCase())).length;
    const speedEvents = missions.reduce((sum, item) => sum + Number(item.hse?.speeding_events || 0), 0);
    const validDocs = documents.filter((item) => (item.status || item.ocr_status?.status || 'valid') !== 'expired').length;
    return { active, delivered, speedEvents, validDocs };
  }, [documents, missions]);

  // Trois listes sur l'écran, chacune lue par pages avec sa propre clé d'adresse.
  const pageLivraisons = usePagination(missions, { cle: 'livraisons' });
  const pageSecurite = usePagination(missions, { cle: 'securite' });
  const pageDocuments = usePagination(documents, { cle: 'documents' });

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="readouts" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Donneur d'ordre"
        title="Portail client"
        description="Vos livraisons de carburant en cours, leurs preuves de livraison et le respect des règles de sécurité sur la route."
        stats={[
          {
            label: 'En route',
            valeur: metrics.active,
            ton: metrics.active ? 'attention' : 'positif',
            aide: 'Livraisons en route',
          },
          { label: 'Terminées', valeur: metrics.delivered, aide: 'Livraisons terminées' },
          {
            label: 'Excès de vitesse',
            valeur: metrics.speedEvents,
            ton: metrics.speedEvents ? 'critique' : 'positif',
          },
          { label: 'Documents valides', valeur: metrics.validDocs },
        ]}
        actions={
          <Button variant="outline" onClick={load}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Actualiser
          </Button>
        }
      />

      <section className="grid gap-4 xl:grid-cols-[1fr_420px]">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-1">
              <CardTitle>Vos livraisons</CardTitle>
              <Aide titre="Vos livraisons">Seules les livraisons qui vous concernent sont affichées.</Aide>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {pageLivraisons.lignes.map((item) => (
              <MissionRow key={item.trip?.id} item={item} />
            ))}
            {missions.length === 0 && (
              <div className="rounded-lg border border-border bg-muted/50 p-5 text-sm font-semibold text-muted-foreground">
                Aucune livraison à afficher pour le moment.
              </div>
            )}
            <BarrePagination pagination={pageLivraisons} nom="livraisons" className="mt-0" />
          </CardContent>
        </Card>

        <aside className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sécurité sur la route</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pageSecurite.lignes.map((item) => (
                <div key={item.trip?.id} className="rounded-lg border border-border bg-card p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-black text-foreground">{referenceLisible(item.cargo?.reference || item.order?.reference_number)}</p>
                    <Badge className={item.hse?.speeding_events ? 'border-flame/30 bg-flame/10 text-flame-ink' : 'border-success/30 bg-success/10 text-success'}>
                      {item.hse?.speeding_events ? 'À surveiller' : 'Conforme'}
                    </Badge>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {Number(item.hse?.speeding_per_100km || 0).toLocaleString('fr-FR')} excès de vitesse pour 100 km
                  </p>
                </div>
              ))}
              <BarrePagination pagination={pageSecurite} nom="contrôles de sécurité" className="mt-0" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pageDocuments.lignes.map((item) => (
                <DocumentRow key={item.id || `${item.document_id}-${item.doc_category}`} item={item} />
              ))}
              {documents.length === 0 && (
                <div className="rounded-lg border border-border bg-muted/50 p-4 text-sm font-semibold text-muted-foreground">
                  Aucun document à afficher pour le moment.
                </div>
              )}
              <BarrePagination pagination={pageDocuments} nom="documents" className="mt-0" />
            </CardContent>
          </Card>
        </aside>
      </section>
    </div>
  );
}

function MissionRow({ item }) {
  const trip = item.trip || {};
  const order = item.order || {};
  const position = item.latest_position || {};
  const cargo = item.cargo || {};
  const status = (trip.derived_global_status || trip.status || '').toUpperCase();
  const blocked = Boolean(trip.is_blocked || ['FAILED_RECONCILIATION', 'BLOCKED_AUDIT', 'POD_REQUIRED'].includes(status));
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={blocked ? 'border-destructive/30 bg-destructive/10 text-destructive' : 'border-success/30 bg-success/10 text-success'}>
              {blocked ? 'En vérification' : libelle('tripStatus', status, 'En cours')}
            </Badge>
            <span className="readout-label">{referenceLisible(cargo.reference || order.reference_number)}</span>
          </div>
          <h2 className="mt-3">
            {texteSur(cargo.delivery_location || order.delivery_location, 'Destination du client')}
          </h2>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-muted-foreground">
            <span>{formatVolume(cargo.delivered_volume_liters || cargo.loaded_volume_liters || trip.fuel_quantity_real || trip.allocated_quantity || 0)}</span>
            <span>Livraison prévue : {cargo.requested_delivery_date ? formatDateHeure(cargo.requested_delivery_date) : 'à confirmer'}</span>
          </div>
        </div>
        <div className="grid gap-2 text-sm text-muted-foreground lg:min-w-[260px]">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-flame-ink" />
            <span>
              {position.latitude && position.longitude
                ? `${Number(position.latitude).toFixed(5)}, ${Number(position.longitude).toFixed(5)}`
                : 'Position non reçue'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4 text-muted-foreground" />
            <span>
              {position.speed_kmh != null ? `${position.speed_kmh} km/h` : 'Vitesse inconnue'}
              {position.timestamp ? ` · ${formatDateHeure(position.timestamp)}` : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentRow({ item }) {
  const isCompliance = Boolean(item.doc_category);
  const label = item.file_name || libelle('documentType', item.doc_category || item.doc_type, 'Document');
  const telechargeable = !isCompliance && Boolean(item.url) && Boolean(item.id);
  const telecharger = async () => {
    try {
      const reponse = await clientPortalAPI.downloadDocument(item.id);
      downloadBlob(reponse, item.file_name || 'document', item.file_type);
    } catch (err) {
      toast.error(messageErreur(err, 'Ce document est momentanément indisponible.'));
    }
  };
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {isCompliance ? <ShieldCheck className="h-4 w-4 text-success" /> : <FileText className="h-4 w-4 text-muted-foreground" />}
            <p className="truncate text-sm font-black text-foreground">{label}</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {item.trip_id || item.associated_trip_id ? 'Lié à une livraison' : 'Conformité de la flotte'} ·{' '}
            {item.expires_at ? `Expire le ${formatDateHeure(item.expires_at)}` : libelle('docExpiry', item.status, 'Valide')}
          </p>
        </div>
        {telechargeable ? (
          <Button size="sm" variant="outline" onClick={telecharger} aria-label="Télécharger le document">
            <FileDown className="h-4 w-4" />
          </Button>
        ) : (
          <CheckCircle2 className="h-5 w-5 text-success" />
        )}
      </div>
    </div>
  );
}

